import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { superadminCodebooksClient, superadminCompaniesClient, superadminUsersClient } from 'api';
import type {
  CappedTableResultOfEventLogEntry,
  SuperadminListEventLogsParams
} from 'api/models/api';
import type {
  ColorPalette,
  IpFilterRulesToEdit,
  SuperadminCompany,
  SuperadminCompanyDetail,
  SuperadminCompanySettingsToEdit,
  SuperadminCompanyToAdd,
  SuperadminCompanyToEdit,
  SuperadminUserSuperadminPermissionResult,
  SuperadminUserSuperadminPermissionToEdit,
  SuperadminUserToAdd,
  SuperadminUserToEdit,
  UserAppearanceToEdit
} from 'api/models/mainservice';
import type { SuperadminCompanyStats } from 'api/models/unified';
import backgroundTasksClient from 'api/superadmin/backgroundTasksClient';
import superadminPermissionsClient from 'api/superadmin/superadminPermissionsClient';
import type {
  ApiError,
  Codebook,
  CodebookToAdd,
  CodebookToEdit,
  CustomCodebook,
  CustomCodebookItem,
  CustomCodebookItemToAdd,
  CustomCodebookItemToEdit,
  CustomCodebookToAdd,
  CustomCodebookToEdit,
  Group,
  GroupToAdd,
  GroupToEdit,
  GroupUsersToManage,
  Licence,
  LicenceToAdd,
  Manufacturer,
  ManufacturerToAdd,
  ManufacturerToEdit,
  OrderRequestCategory,
  OrderRequestCategoryToAdd,
  OrderRequestCategoryToEdit,
  ServiceObjectCategoryWithStringTemplate,
  ServiceObjectCategoryWithStringTemplateToAdd,
  ServiceObjectCategoryWithStringTemplateToEdit,
  Skill,
  SkillToAdd,
  SkillUsersToManage,
  StorageDownloadTemplates,
  TableResult,
  VatOption,
  VatOptionToAdd,
  VatOptionToEdit,
  WorkItem,
  WorkItemToAdd,
  WorkItemToEdit
} from 'common/models';
import { GLOBAL_QUERY_STALE_TIME } from 'common/queries';
import type {
  NotificationContentScope,
  NotificationContentSettings,
  SetNotificationContentRequest
} from 'components/NotificationsContent/models';
import { defaultEventLogQuery } from 'helpers/auditHelper';
import { handleApiErrorWithNotification } from 'helpers/errorHandling';
import { getInfiniteNextPageParamV2 } from 'helpers/queriesHelper';
import { getTableQueryKey } from 'helpers/tableHelpers';
import type { CompanyPreferences } from 'pages/settings/companySettings/models';

import type {
  BackgroundTaskState,
  SuperadminBackgroundTaskInfo,
  SuperadminBackgroundTaskInfoDetail,
  SuperadminBackgroundTaskInfoQuery
} from './components/operations/models';
import type {
  Company,
  CompanyCodebooks,
  CompanyDetail,
  CompanyTableQuery,
  SuperadminCompanyCodebookItem,
  SuperadminCompanyCodebooksToSet,
  SuperadminCustomFormTemplate,
  SuperadminCustomFormTemplateToAdd,
  SuperadminCustomFormTemplateToEdit,
  SuperadminUser
} from './models';

const companiesKey = 'companies';
const superadminDownloadUriTemplates = 'superadminDownloadUriTemplates';
export const backgroundTasksKey = 'superadminBackgroundTasks';
const backgroundTaskDetailKey = 'superadminBackgroundTaskDetail';
const usersPermissions = 'usersPermissions';
const companyStatisticsKey = 'companyStatistics';
const companyUserAvailableColorPaletteKey = 'companyUserAvailableColorPalette';
const companyEventLogsKey = 'companyEventLogs';

export const getCompanyNotificationsQueryKeyBuilder = (id: string) => ['companyNotification', id];
export const getCompaniesQueryKeyBuilder = () => [companiesKey];
export const getCompanyDetailQueryKeyBuilder = (id: string) => ['companyDetail', id];
export const getCompanyCodebooksQueryKeyBuilder = (id: string) => ['companyCodebooks', id];
export const getCompaniesCodebookQueryKeyBuilder = () => [companiesKey, 'codebook'];
export const getSuperadminDownloadUriTemplatesKeyBuilder = (companyId: string) => [
  superadminDownloadUriTemplates,
  companyId
];
export const getSuperadminBackgroundTaskDetailKeyBuilder = (taskId: string) => [
  backgroundTaskDetailKey,
  taskId
];

export const getCompanyUsersPermissionsKeyBuilder = (id: string) => [usersPermissions, id];
export const getCompanyStatisticsKeyBuilder = (id: string) => [companyStatisticsKey, id];
export const getCompanyUserAvailableColorPaletteKeyBuilder = (id: string) => [
  companyUserAvailableColorPaletteKey,
  id
];

export const getCompanyBackgroundTasksKeyBuilder = (companyId: string) => [
  backgroundTasksKey,
  companyId
];

export const useGetCompaniesQuery = (params: CompanyTableQuery) => {
  return useQuery<TableResult<SuperadminCompany>, ApiError>(
    getTableQueryKey(companiesKey, params),
    () => superadminCompaniesClient.getCompanies(params),
    {
      keepPreviousData: true
    }
  );
};

export const useGetCompanyDetailQuery = (id: string) => {
  return useQuery<SuperadminCompanyDetail, ApiError>(getCompanyDetailQueryKeyBuilder(id), () =>
    superadminCompaniesClient.getCompanyDetail(id)
  );
};

export const useAddCompanyMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<Company, ApiError, SuperadminCompanyToAdd>(
    superadminCompaniesClient.addCompany,
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(getCompaniesQueryKeyBuilder());
      }
    }
  );
};

export const useEditCompanyMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<undefined, ApiError, { companyId: string; values: SuperadminCompanyToEdit }>(
    superadminCompaniesClient.editCompany,
    {
      onSuccess: (data, { companyId }) =>
        queryClient.invalidateQueries(getCompanyDetailQueryKeyBuilder(companyId))
    }
  );
};

export const useDeleteCompanyMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<undefined, ApiError, string>(superadminCompaniesClient.deleteCompany, {
    onSuccess: async () => {
      await queryClient.invalidateQueries(getCompaniesQueryKeyBuilder());
    },
    onError: (error) => handleApiErrorWithNotification(error)
  });
};

export const useSuperadminSetCompanyLogo = () => {
  const queryClient = useQueryClient();

  return (companyId: string, logoUrl: string) =>
    queryClient.setQueryData<CompanyDetail | undefined>(
      getCompanyDetailQueryKeyBuilder(companyId),
      (oldData) =>
        oldData && {
          ...oldData,
          logoUrl
        }
    );
};

export const useSuperadminSetCompanyHeaderLogo = () => {
  const queryClient = useQueryClient();

  return (companyId: string, headerLogoUrl: string) =>
    queryClient.setQueryData<CompanyDetail | undefined>(
      getCompanyDetailQueryKeyBuilder(companyId),
      (oldData) =>
        oldData && {
          ...oldData,
          headerLogoUrl
        }
    );
};

export const useSuperadminDeleteCompanyHeaderLogoMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<CompanyDetail, ApiError, string>(superadminCompaniesClient.deleteHeaderLogo, {
    onSuccess: (data, companyId) => {
      queryClient.setQueryData<CompanyDetail>(getCompanyDetailQueryKeyBuilder(companyId), data);
    }
  });
};

export const useSuperadminSetCompanyStamp = () => {
  const queryClient = useQueryClient();

  return (companyId: string, stampUrl: string) =>
    queryClient.setQueryData<CompanyDetail | undefined>(
      getCompanyDetailQueryKeyBuilder(companyId),
      (oldData) =>
        oldData && {
          ...oldData,
          stampUrl
        }
    );
};

export const useSuperadminDeleteCompanyStampMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<CompanyDetail, ApiError, string>(superadminCompaniesClient.deleteStamp, {
    onSuccess: (data, companyId) => {
      queryClient.setQueryData<CompanyDetail>(getCompanyDetailQueryKeyBuilder(companyId), data);
    }
  });
};

export const useSuperadminDeleteCompanyLogoMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<CompanyDetail, ApiError, string>(superadminCompaniesClient.deleteLogo, {
    onSuccess: (data, companyId) => {
      queryClient.setQueryData<CompanyDetail>(getCompanyDetailQueryKeyBuilder(companyId), data);
    }
  });
};

export const useUpdateCompanySettingsMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    undefined,
    ApiError,
    {
      settings: Partial<SuperadminCompanySettingsToEdit>;
      companyId: string;
    }
  >(superadminCompaniesClient.updateCompanySettings, {
    onSuccess: async (data, { companyId }) => {
      await queryClient.invalidateQueries(getCompanyDetailQueryKeyBuilder(companyId));
    }
  });
};

export const useEditCompanyIpFilterRulesMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<undefined, ApiError, { companyId: string; data: IpFilterRulesToEdit }>({
    mutationFn: superadminCompaniesClient.editIpFilterRules,
    onSuccess: (data, { companyId }) =>
      queryClient.invalidateQueries(getCompanyDetailQueryKeyBuilder(companyId))
  });
};

export const useDisableCompanyMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<undefined, ApiError, string>(superadminCompaniesClient.disableCompany, {
    onSuccess: (data, companyId) => {
      queryClient.setQueryData<CompanyDetail | undefined>(
        getCompanyDetailQueryKeyBuilder(companyId),
        (oldData) =>
          oldData && {
            ...oldData,
            isDisabled: true
          }
      );
    },
    onError: (error) => handleApiErrorWithNotification(error)
  });
};

export const useEnableCompanyMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<undefined, ApiError, string>(superadminCompaniesClient.enableCompany, {
    onSuccess: (data, companyId) => {
      queryClient.setQueryData<CompanyDetail | undefined>(
        getCompanyDetailQueryKeyBuilder(companyId),
        (oldData) =>
          oldData && {
            ...oldData,
            isDisabled: false
          }
      );
    },
    onError: (error) => handleApiErrorWithNotification(error)
  });
};

// Licence

export const useAddCompanyLicenceMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<Licence[], ApiError, LicenceToAdd>(superadminCompaniesClient.addLicence, {
    onSuccess: (data, { companyId }) => {
      queryClient.setQueryData<CompanyDetail | undefined>(
        getCompanyDetailQueryKeyBuilder(companyId),
        (oldData) =>
          oldData && {
            ...oldData,
            licences: data
          }
      );
    }
  });
};

export const useToggleCompanyLicenceMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    undefined,
    ApiError,
    { licenceId: string; companyId: string; isActive: boolean }
  >(({ licenceId }) => superadminCompaniesClient.toggleLicenceActive(licenceId), {
    onSuccess: (data, { companyId, licenceId, isActive }) => {
      queryClient.setQueryData<CompanyDetail | undefined>(
        getCompanyDetailQueryKeyBuilder(companyId),
        (oldData) =>
          oldData && {
            ...oldData,
            licences: oldData.licences.map((licence) => {
              if (licence.id === licenceId) {
                return {
                  ...licence,
                  isActive
                };
              }
              return licence;
            })
          }
      );
    }
  });
};

// CustomFormTemplate

export const useAddCustomFormTemplateMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    SuperadminCustomFormTemplate,
    ApiError,
    {
      data: SuperadminCustomFormTemplateToAdd;
      companyId: string;
    }
  >(superadminCompaniesClient.addCustomFormTemplate, {
    onSuccess: (data, { companyId }) => {
      queryClient.setQueryData<CompanyDetail | undefined>(
        getCompanyDetailQueryKeyBuilder(companyId),
        (oldData) =>
          oldData && {
            ...oldData,
            customFormTemplates: [...oldData.customFormTemplates, data]
          }
      );
    }
  });
};

export const useEditCustomFormTemplateMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    SuperadminCustomFormTemplate,
    ApiError,
    {
      data: SuperadminCustomFormTemplateToEdit;
      companyId: string;
      templateId: string;
    }
  >(superadminCompaniesClient.editCustomFormTemplate, {
    onSuccess: (data, { companyId, templateId }) => {
      queryClient.setQueryData<CompanyDetail | undefined>(
        getCompanyDetailQueryKeyBuilder(companyId),
        (oldData) =>
          oldData && {
            ...oldData,
            customFormTemplates: oldData.customFormTemplates.map((template) => {
              if (template.id === templateId) {
                return {
                  ...template,
                  ...data
                };
              }

              return template;
            })
          }
      );
    }
  });
};

export const useDeleteCustomFormTemplateMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    undefined,
    ApiError,
    {
      companyId: string;
      templateId: string;
    }
  >(superadminCompaniesClient.deleteCustomFormTemplate, {
    onSuccess: (data, { companyId, templateId }) => {
      queryClient.setQueryData<CompanyDetail | undefined>(
        getCompanyDetailQueryKeyBuilder(companyId),
        (oldData) =>
          oldData && {
            ...oldData,
            customFormTemplates: oldData.customFormTemplates.filter((x) => x.id !== templateId)
          }
      );
    },
    onError: (error) => handleApiErrorWithNotification(error)
  });
};

export const useDeleteAllCustomFormTemplateMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<undefined, ApiError, string>(
    superadminCompaniesClient.deleteAllCustomFormTemplates,
    {
      onSuccess: (data, companyId) => {
        queryClient.setQueryData<CompanyDetail | undefined>(
          getCompanyDetailQueryKeyBuilder(companyId),
          (oldData) =>
            oldData && {
              ...oldData,
              customFormTemplates: []
            }
        );
      },
      onError: (error) => handleApiErrorWithNotification(error)
    }
  );
};

export const useUpdateCustomFormPlannedInterventions = () => {
  return useMutation<
    undefined,
    ApiError,
    {
      companyId: string;
      templateId: string;
    }
  >(superadminCompaniesClient.updateCustomFormPlannedInterventions, {
    onError: (error) => handleApiErrorWithNotification(error)
  });
};

export const useUpdatePlannedInterventions = () => {
  return useMutation<
    undefined,
    ApiError,
    {
      companyId: string;
    }
  >(superadminCompaniesClient.updatePlannedInterventions, {
    onError: (error) => handleApiErrorWithNotification(error)
  });
};

// Users

export const useGetCompanyUserAvailableColorPaletteQuery = (companyId: string) => {
  return useQuery<ColorPalette, ApiError>({
    queryKey: getCompanyUserAvailableColorPaletteKeyBuilder(companyId),
    queryFn: () => superadminUsersClient.nextAvailableColor(companyId),
    refetchOnWindowFocus: false
  });
};

export const useAddCompanyUserMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    SuperadminUser,
    ApiError,
    { companyId: string; userToAdd: SuperadminUserToAdd }
  >(superadminUsersClient.addUser, {
    onSuccess: async (data, { companyId }) => {
      queryClient.setQueryData<CompanyDetail | undefined>(
        getCompanyDetailQueryKeyBuilder(companyId),
        (oldData) =>
          oldData && {
            ...oldData,
            userCount: oldData.userCount + 1,
            users: [...oldData.users, data]
          }
      );
      await queryClient.invalidateQueries(getCompanyUserAvailableColorPaletteKeyBuilder(companyId));
    }
  });
};

export const useEditCompanyUserMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    undefined,
    ApiError,
    {
      companyId: string;
      userId: string;
      userToEdit: SuperadminUserToEdit;
    }
  >(superadminUsersClient.editUser, {
    onSuccess: (data, { companyId }) =>
      queryClient.invalidateQueries(getCompanyDetailQueryKeyBuilder(companyId))
  });
};

export const useDeleteCompanyUserMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    undefined,
    ApiError,
    {
      companyId: string;
      userId: string;
    }
  >(superadminUsersClient.deleteUser, {
    onSuccess: (data, { companyId, userId }) => {
      queryClient.setQueryData<CompanyDetail | undefined>(
        getCompanyDetailQueryKeyBuilder(companyId),
        (oldData) =>
          oldData && {
            ...oldData,
            users: oldData.users.map((user) => {
              if (user.id === userId) {
                return {
                  ...user,
                  isDeleted: true
                };
              }

              return user;
            })
          }
      );
    },
    onError: (error) => handleApiErrorWithNotification(error)
  });
};

export const useDisableCompanyUserMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    undefined,
    ApiError,
    {
      companyId: string;
      userId: string;
    }
  >(superadminUsersClient.disableUser, {
    onSuccess: (data, { companyId, userId }) => {
      queryClient.setQueryData<CompanyDetail | undefined>(
        getCompanyDetailQueryKeyBuilder(companyId),
        (oldData) =>
          oldData && {
            ...oldData,
            users: oldData.users.map((user) => {
              if (user.id === userId) {
                return {
                  ...user,
                  isDisabled: true
                };
              }

              return user;
            })
          }
      );
    },
    onError: (error) => handleApiErrorWithNotification(error)
  });
};

export const useEnableCompanyUserMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    undefined,
    ApiError,
    {
      companyId: string;
      userId: string;
    }
  >(superadminUsersClient.enableUser, {
    onSuccess: (data, { companyId, userId }) => {
      queryClient.setQueryData<CompanyDetail | undefined>(
        getCompanyDetailQueryKeyBuilder(companyId),
        (oldData) =>
          oldData && {
            ...oldData,
            users: oldData.users.map((user) => {
              if (user.id === userId) {
                return {
                  ...user,
                  isDisabled: false
                };
              }

              return user;
            })
          }
      );
    },
    onError: (error) => handleApiErrorWithNotification(error)
  });
};

export const useDeleteCompanyUserProfilePictureMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<undefined, ApiError, { userId: string; companyId: string }>({
    mutationFn: ({ userId, companyId }) =>
      superadminUsersClient.deleteCompanyUserProfilePicture(userId, companyId),
    onSuccess: async (data, { companyId }) => {
      await queryClient.invalidateQueries(getCompanyDetailQueryKeyBuilder(companyId));
    }
  });
};

export const useChangeCompanyUserAppearanceMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    undefined,
    ApiError,
    { companyId: string; userId: string; appearance: UserAppearanceToEdit }
  >({
    mutationFn: ({ companyId, userId, appearance }) =>
      superadminUsersClient.changeAppearance({ companyId, userId, appearance }),
    onSuccess: async (data, { companyId }) => {
      await queryClient.invalidateQueries(getCompanyDetailQueryKeyBuilder(companyId));
    }
  });
};

// Company notifications

export const useGetCompanyNotificationsQuery = (companyId: string) => {
  return useQuery<NotificationContentSettings, ApiError>(
    getCompanyNotificationsQueryKeyBuilder(companyId),
    () => superadminCompaniesClient.getNotifications(companyId)
  );
};

export const useEditCompanyPreferencesMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<undefined, ApiError, { companyId: string; preferences: CompanyPreferences }>(
    superadminCompaniesClient.editCompanyPreferences,
    {
      onSuccess: (data, { companyId, preferences }) => {
        queryClient.setQueryData<NotificationContentSettings | undefined>(
          getCompanyNotificationsQueryKeyBuilder(companyId),
          (oldData) => oldData && { ...oldData, companyPreferences: preferences }
        );
      }
    }
  );
};

export const useEditCompanyNotificationMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    undefined,
    ApiError,
    {
      companyId: string;
      scope: NotificationContentScope;
      notificationId: string;
      // Using local interface, generated with apigen is incorrect because of .Net core version
      values: SetNotificationContentRequest;
    }
  >(superadminCompaniesClient.editNotification, {
    onSuccess: (data, { companyId, notificationId, scope, values }) => {
      queryClient.setQueryData<NotificationContentSettings | undefined>(
        getCompanyNotificationsQueryKeyBuilder(companyId),
        (oldData) =>
          oldData && {
            ...oldData,
            [scope]: oldData[scope].map((notificationItem) => {
              if (notificationItem.id === notificationId) {
                return {
                  ...notificationItem,
                  calendarEvent: {
                    ...notificationItem.calendarEvent,
                    title: {
                      defaultValue: notificationItem.calendarEvent?.title.defaultValue,
                      value: values.calendarEvent?.title
                    },
                    description: {
                      defaultValue: notificationItem.calendarEvent?.description.defaultValue,
                      value: values.calendarEvent?.description
                    }
                  },
                  email: {
                    ...notificationItem.email,
                    Subject: {
                      defaultValue: notificationItem.email?.Subject?.defaultValue,
                      value: values.email?.Subject
                    },
                    Text: {
                      defaultValue: notificationItem.email?.Text?.defaultValue,
                      value: values.email?.Text
                    }
                  },
                  notification: {
                    ...notificationItem.notification,
                    title: {
                      defaultValue: notificationItem.notification?.title.defaultValue,
                      value: values.notification?.title
                    },
                    message: {
                      defaultValue: notificationItem.notification?.message.defaultValue,
                      value: values.notification?.message
                    }
                  }
                };
              }

              return notificationItem;
            })
          }
      );
    }
  });
};

// Company logs

export const useGetCompanyEventLogsQuery = (params: SuperadminListEventLogsParams) => {
  return useInfiniteQuery<CappedTableResultOfEventLogEntry, ApiError>({
    queryKey: getTableQueryKey(companyEventLogsKey, {
      ...params,
      page: params.page || defaultEventLogQuery.page,
      pageSize: params.pageSize || defaultEventLogQuery.pageSize
    }),
    queryFn: ({ pageParam = 1 }) =>
      superadminCompaniesClient.getCompanyEvents({ ...params, page: pageParam }),
    getNextPageParam: getInfiniteNextPageParamV2
  });
};

// Company codebooks

export const useGetCompanyCodebooksQuery = (companyId: string) => {
  return useQuery<CompanyCodebooks, ApiError>(
    getCompanyCodebooksQueryKeyBuilder(companyId),
    () => superadminCodebooksClient.getCodebooks(companyId),
    { enabled: !!companyId }
  );
};

// Customer category

export const useAddCompanyCustomerCategoryMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    Codebook,
    ApiError,
    {
      companyId: string;
      values: CodebookToAdd;
    }
  >(superadminCodebooksClient.addCustomerCategory, {
    onSuccess: (data, { companyId }) => {
      queryClient.setQueryData<CompanyCodebooks | undefined>(
        getCompanyCodebooksQueryKeyBuilder(companyId),
        (oldData) =>
          oldData && { ...oldData, customerCategories: [...oldData.customerCategories, data] }
      );
    }
  });
};

export const useEditCompanyCustomerCategoryMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    undefined,
    ApiError,
    { companyId: string; categoryId: string; values: CodebookToEdit }
  >(superadminCodebooksClient.editCustomerCategory, {
    onSuccess: (data, { companyId, categoryId, values }) => {
      queryClient.setQueryData<CompanyCodebooks | undefined>(
        getCompanyCodebooksQueryKeyBuilder(companyId),
        (oldData) =>
          oldData && {
            ...oldData,
            customerCategories: oldData.customerCategories.map((category) => {
              if (category.id === categoryId) {
                return { ...category, ...values };
              }
              return category;
            })
          }
      );
    }
  });
};

export const useDeleteCompanyCustomerCategoryMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<undefined, ApiError, { companyId: string; categoryId: string }>(
    superadminCodebooksClient.deleteCustomerCategory,
    {
      onSuccess: (data, { categoryId, companyId }) => {
        queryClient.setQueryData<CompanyCodebooks | undefined>(
          getCompanyCodebooksQueryKeyBuilder(companyId),
          (oldData) =>
            oldData && {
              ...oldData,
              customerCategories: oldData.customerCategories.filter(
                (category) => category.id !== categoryId
              )
            }
        );
      },
      onError: (error) => handleApiErrorWithNotification(error)
    }
  );
};

// Customer type

export const useAddCompanyCustomerTypeMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<Codebook, ApiError, { companyId: string; values: CodebookToAdd }>(
    superadminCodebooksClient.addCustomerType,
    {
      onSuccess: (data, { companyId }) => {
        queryClient.setQueryData<CompanyCodebooks | undefined>(
          getCompanyCodebooksQueryKeyBuilder(companyId),
          (oldData) => oldData && { ...oldData, customerTypes: [...oldData.customerTypes, data] }
        );
      }
    }
  );
};

export const useEditCompanyCustomerTypeMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    undefined,
    ApiError,
    { companyId: string; typeId: string; values: CodebookToEdit }
  >(superadminCodebooksClient.editCustomerType, {
    onSuccess: (data, { companyId, typeId, values }) => {
      queryClient.setQueryData<CompanyCodebooks | undefined>(
        getCompanyCodebooksQueryKeyBuilder(companyId),
        (oldData) =>
          oldData && {
            ...oldData,
            customerTypes: oldData.customerTypes.map((type) => {
              if (type.id === typeId) {
                return { ...type, ...values };
              }
              return type;
            })
          }
      );
    }
  });
};

export const useDeleteCompanyCustomerTypeMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<undefined, ApiError, { companyId: string; typeId: string }>(
    superadminCodebooksClient.deleteCustomerType,
    {
      onSuccess: (data, { companyId, typeId }) => {
        queryClient.setQueryData<CompanyCodebooks | undefined>(
          getCompanyCodebooksQueryKeyBuilder(companyId),
          (oldData) =>
            oldData && {
              ...oldData,
              customerTypes: oldData.customerTypes.filter((type) => type.id !== typeId)
            }
        );
      },
      onError: (error) => handleApiErrorWithNotification(error)
    }
  );
};

// Material category

export const useAddCompanyMaterialCategoryMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<Codebook, ApiError, { companyId: string; values: CodebookToAdd }>(
    superadminCodebooksClient.addMaterialCategory,
    {
      onSuccess: (data, { companyId }) => {
        queryClient.setQueryData<CompanyCodebooks | undefined>(
          getCompanyCodebooksQueryKeyBuilder(companyId),
          (oldData) =>
            oldData && { ...oldData, materialCategories: [...oldData.materialCategories, data] }
        );
      }
    }
  );
};

export const useEditCompanyMaterialCategoryMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    undefined,
    ApiError,
    {
      companyId: string;
      categoryId: string;
      values: CodebookToEdit;
    }
  >(superadminCodebooksClient.editMaterialCategory, {
    onSuccess: (data, { companyId, categoryId, values }) => {
      queryClient.setQueryData<CompanyCodebooks | undefined>(
        getCompanyCodebooksQueryKeyBuilder(companyId),
        (oldData) =>
          oldData && {
            ...oldData,
            materialCategories: oldData.materialCategories.map((type) => {
              if (type.id === categoryId) {
                return { ...type, ...values };
              }
              return type;
            })
          }
      );
    }
  });
};

export const useDeleteCompanyMaterialCategoryMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<undefined, ApiError, { companyId: string; categoryId: string }>(
    superadminCodebooksClient.deleteMaterialCategory,
    {
      onSuccess: (data, { categoryId, companyId }) => {
        queryClient.setQueryData<CompanyCodebooks | undefined>(
          getCompanyCodebooksQueryKeyBuilder(companyId),
          (oldData) =>
            oldData && {
              ...oldData,
              materialCategories: oldData.materialCategories.filter(
                (category) => category.id !== categoryId
              )
            }
        );
      },
      onError: (error) => handleApiErrorWithNotification(error)
    }
  );
};

// Material supplier

export const useAddCompanyMaterialSupplierMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    Codebook,
    ApiError,
    {
      companyId: string;
      values: CodebookToAdd;
    }
  >(superadminCodebooksClient.addMaterialSupplier, {
    onSuccess: (data, { companyId }) => {
      queryClient.setQueryData<CompanyCodebooks | undefined>(
        getCompanyCodebooksQueryKeyBuilder(companyId),
        (oldData) =>
          oldData && { ...oldData, materialSuppliers: [...oldData.materialSuppliers, data] }
      );
    }
  });
};

export const useEditCompanyMaterialSupplierMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    undefined,
    ApiError,
    {
      companyId: string;
      supplierId: string;
      values: CodebookToEdit;
    }
  >(superadminCodebooksClient.editMaterialSupplier, {
    onSuccess: (data, { companyId, supplierId, values }) => {
      queryClient.setQueryData<CompanyCodebooks | undefined>(
        getCompanyCodebooksQueryKeyBuilder(companyId),
        (oldData) =>
          oldData && {
            ...oldData,
            materialSuppliers: oldData.materialSuppliers.map((supplier) => {
              if (supplier.id === supplierId) {
                return { ...supplier, ...values };
              }
              return supplier;
            })
          }
      );
    }
  });
};

export const useDeleteCompanyMaterialSupplierMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<undefined, ApiError, { companyId: string; supplierId: string }>(
    superadminCodebooksClient.deleteMaterialSupplier,
    {
      onSuccess: (data, { companyId, supplierId }) => {
        queryClient.setQueryData<CompanyCodebooks | undefined>(
          getCompanyCodebooksQueryKeyBuilder(companyId),
          (oldData) =>
            oldData && {
              ...oldData,
              materialSuppliers: oldData.materialSuppliers.filter(
                (supplier) => supplier.id !== supplierId
              )
            }
        );
      },
      onError: (error) => handleApiErrorWithNotification(error)
    }
  );
};

// Service Object Manufacturer

export const useAddCompanyServiceObjectManufacturerMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    Manufacturer,
    ApiError,
    {
      companyId: string;
      values: ManufacturerToAdd;
    }
  >(superadminCodebooksClient.addServiceObjectManufacturer, {
    onSuccess: (data, { companyId }) => {
      queryClient.setQueryData<CompanyCodebooks | undefined>(
        getCompanyCodebooksQueryKeyBuilder(companyId),
        (oldData) =>
          oldData && {
            ...oldData,
            serviceObjectManufacturers: [...oldData.serviceObjectManufacturers, data]
          }
      );
    }
  });
};

export const useEditCompanyServiceObjectManufacturerMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    undefined,
    ApiError,
    {
      companyId: string;
      manufacturerId: string;
      values: ManufacturerToEdit;
    }
  >(superadminCodebooksClient.editServiceObjectManufacturer, {
    onSuccess: (data, { companyId, manufacturerId, values }) => {
      queryClient.setQueryData<CompanyCodebooks | undefined>(
        getCompanyCodebooksQueryKeyBuilder(companyId),
        (oldData) =>
          oldData && {
            ...oldData,
            serviceObjectManufacturers: oldData.serviceObjectManufacturers.map((manufacturer) => {
              if (manufacturer.id === manufacturerId) {
                return { ...manufacturer, ...values };
              }
              return manufacturer;
            })
          }
      );
    }
  });
};

export const useDeleteCompanyServiceObjectManufacturerMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    undefined,
    ApiError,
    {
      companyId: string;
      manufacturerId: string;
    }
  >(superadminCodebooksClient.deleteServiceObjectManufacturer, {
    onSuccess: (data, { companyId, manufacturerId }) => {
      queryClient.setQueryData<CompanyCodebooks | undefined>(
        getCompanyCodebooksQueryKeyBuilder(companyId),
        (oldData) =>
          oldData && {
            ...oldData,
            serviceObjectManufacturers: oldData.serviceObjectManufacturers.filter(
              (manufacturer) => manufacturer.id !== manufacturerId
            )
          }
      );
    },
    onError: (error) => handleApiErrorWithNotification(error)
  });
};

// Work Item

export const useAddCompanyWorkItemMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<WorkItem, ApiError, { companyId: string; values: WorkItemToAdd }>(
    superadminCodebooksClient.addWorkItem,
    {
      onSuccess: (data, { companyId }) => {
        queryClient.setQueryData<CompanyCodebooks | undefined>(
          getCompanyCodebooksQueryKeyBuilder(companyId),
          (oldData) =>
            oldData && {
              ...oldData,
              workItems: [...oldData.workItems, data]
            }
        );
      }
    }
  );
};

export const useEditCompanyWorkItemMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    undefined,
    ApiError,
    {
      companyId: string;
      workItemId: string;
      values: WorkItemToEdit;
    }
  >(superadminCodebooksClient.editWorkItem, {
    onSuccess: (data, { companyId, values, workItemId }) => {
      queryClient.setQueryData<CompanyCodebooks | undefined>(
        getCompanyCodebooksQueryKeyBuilder(companyId),
        (oldData) =>
          oldData && {
            ...oldData,
            workItems: oldData.workItems.map((workItem) => {
              if (workItem.id === workItemId) {
                return { ...workItem, ...values };
              }
              return workItem;
            })
          }
      );
    }
  });
};

export const useDeleteCompanyWorkItemMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<undefined, ApiError, { companyId: string; workItemId: string }>(
    superadminCodebooksClient.deleteWorkItem,
    {
      onSuccess: (data, { companyId, workItemId }) => {
        queryClient.setQueryData<CompanyCodebooks | undefined>(
          getCompanyCodebooksQueryKeyBuilder(companyId),
          (oldData) =>
            oldData && {
              ...oldData,
              workItems: oldData.workItems.filter((workItem) => workItem.id !== workItemId)
            }
        );
      },
      onError: (error) => handleApiErrorWithNotification(error)
    }
  );
};

// Vat Option

export const useAddCompanyVatOptionMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<VatOption, ApiError, { companyId: string; values: VatOptionToAdd }>(
    superadminCodebooksClient.addVatOption,
    {
      onSuccess: (data, { companyId }) => {
        queryClient.setQueryData<CompanyCodebooks | undefined>(
          getCompanyCodebooksQueryKeyBuilder(companyId),
          (oldData) =>
            oldData && {
              ...oldData,
              vatOptions: [...oldData.vatOptions, data]
            }
        );
      }
    }
  );
};

export const useEditCompanyVatOptionMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    undefined,
    ApiError,
    {
      companyId: string;
      vatOptionId: string;
      values: VatOptionToEdit;
    }
  >(superadminCodebooksClient.editVatOption, {
    onSuccess: (data, { companyId, values, vatOptionId }) => {
      queryClient.setQueryData<CompanyCodebooks | undefined>(
        getCompanyCodebooksQueryKeyBuilder(companyId),
        (oldData) =>
          oldData && {
            ...oldData,
            vatOptions: oldData.vatOptions.map((vatOption) => {
              if (vatOption.id === vatOptionId) {
                return { ...vatOption, ...values };
              }
              return vatOption;
            })
          }
      );
    }
  });
};

export const useDeleteCompanyVatOptionMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<undefined, ApiError, { companyId: string; vatOptionId: string }>(
    superadminCodebooksClient.deleteVatOption,
    {
      onSuccess: (data, { companyId, vatOptionId }) => {
        queryClient.setQueryData<CompanyCodebooks | undefined>(
          getCompanyCodebooksQueryKeyBuilder(companyId),
          (oldData) =>
            oldData && {
              ...oldData,
              vatOptions: oldData.vatOptions.filter((vatOption) => vatOption.id !== vatOptionId)
            }
        );
      },
      onError: (error) => handleApiErrorWithNotification(error)
    }
  );
};

// Payment type

export const useAddCompanyPaymentTypeMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<Codebook, ApiError, { companyId: string; values: CodebookToAdd }>(
    superadminCodebooksClient.addPaymentType,
    {
      onSuccess: (data, { companyId }) => {
        queryClient.setQueryData<CompanyCodebooks | undefined>(
          getCompanyCodebooksQueryKeyBuilder(companyId),
          (oldData) =>
            oldData && {
              ...oldData,
              paymentTypes: [...oldData.paymentTypes, data]
            }
        );
      }
    }
  );
};

export const useEditCompanyPaymentTypeMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    undefined,
    ApiError,
    {
      companyId: string;
      paymentTypeId: string;
      values: CodebookToEdit;
    }
  >(superadminCodebooksClient.editPaymentType, {
    onSuccess: (data, { companyId, values, paymentTypeId }) => {
      queryClient.setQueryData<CompanyCodebooks | undefined>(
        getCompanyCodebooksQueryKeyBuilder(companyId),
        (oldData) =>
          oldData && {
            ...oldData,
            paymentTypes: oldData.paymentTypes.map((paymentType) => {
              if (paymentType.id === paymentTypeId) {
                return { ...paymentType, ...values };
              }
              return paymentType;
            })
          }
      );
    }
  });
};

export const useDeleteCompanyPaymentTypeMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<undefined, ApiError, { companyId: string; paymentTypeId: string }>(
    superadminCodebooksClient.deletePaymentType,
    {
      onSuccess: (_, { companyId, paymentTypeId }) => {
        queryClient.setQueryData<CompanyCodebooks | undefined>(
          getCompanyCodebooksQueryKeyBuilder(companyId),
          (oldData) =>
            oldData && {
              ...oldData,
              paymentTypes: oldData.paymentTypes.filter(
                (paymentType) => paymentType.id !== paymentTypeId
              )
            }
        );
      },
      onError: (error) => handleApiErrorWithNotification(error)
    }
  );
};

// Calendar Event Category

export const useAddCompanyCalendarEventCategoryMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<Codebook, ApiError, { companyId: string; values: CodebookToAdd }>(
    superadminCodebooksClient.addCalendarEventCategory,
    {
      onSuccess: (data, { companyId }) => {
        queryClient.setQueryData<CompanyCodebooks | undefined>(
          getCompanyCodebooksQueryKeyBuilder(companyId),
          (oldData) =>
            oldData && {
              ...oldData,
              calendarEventCategories: [...oldData.calendarEventCategories, data]
            }
        );
      }
    }
  );
};

export const useEditCompanyCalendarEventCategoryMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    undefined,
    ApiError,
    {
      companyId: string;
      calendarEventCategoryId: string;
      values: CodebookToEdit;
    }
  >(superadminCodebooksClient.editCalendarEventCategory, {
    onSuccess: (data, { companyId, values, calendarEventCategoryId }) => {
      queryClient.setQueryData<CompanyCodebooks | undefined>(
        getCompanyCodebooksQueryKeyBuilder(companyId),
        (oldData) =>
          oldData && {
            ...oldData,
            calendarEventCategories: oldData.calendarEventCategories.map(
              (calendarEventCategory) => {
                if (calendarEventCategory.id === calendarEventCategoryId) {
                  return { ...calendarEventCategory, ...values };
                }
                return calendarEventCategory;
              }
            )
          }
      );
    }
  });
};

export const useDeleteCompanyCalendarEventCategoryMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<undefined, ApiError, { companyId: string; calendarEventCategoryId: string }>(
    superadminCodebooksClient.deleteCalendarEventCategory,
    {
      onSuccess: (_, { companyId, calendarEventCategoryId }) => {
        queryClient.setQueryData<CompanyCodebooks | undefined>(
          getCompanyCodebooksQueryKeyBuilder(companyId),
          (oldData) =>
            oldData && {
              ...oldData,
              calendarEventCategories: oldData.calendarEventCategories.filter(
                (calendarEventCategory) => calendarEventCategory.id !== calendarEventCategoryId
              )
            }
        );
      },
      onError: (error) => handleApiErrorWithNotification(error)
    }
  );
};

// Calendar Event Category

export const useAddCompanyOrderRequestCategoryMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    OrderRequestCategory,
    ApiError,
    { companyId: string; values: OrderRequestCategoryToAdd }
  >(superadminCodebooksClient.addOrderRequestCategory, {
    onSuccess: (data, { companyId }) => {
      queryClient.setQueryData<CompanyCodebooks | undefined>(
        getCompanyCodebooksQueryKeyBuilder(companyId),
        (oldData) =>
          oldData && {
            ...oldData,
            orderRequestCategories: [...oldData.orderRequestCategories, data]
          }
      );
    }
  });
};

export const useEditCompanyOrderRequestCategoryMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    undefined,
    ApiError,
    {
      companyId: string;
      orderRequestCategoryId: string;
      values: OrderRequestCategoryToEdit;
    }
  >(superadminCodebooksClient.editOrderRequestCategory, {
    onSuccess: (data, { companyId, values, orderRequestCategoryId }) => {
      queryClient.setQueryData<CompanyCodebooks | undefined>(
        getCompanyCodebooksQueryKeyBuilder(companyId),
        (oldData) =>
          oldData && {
            ...oldData,
            orderRequestCategories: oldData.orderRequestCategories.map((orderRequestCategory) => {
              if (orderRequestCategory.id === orderRequestCategoryId) {
                return { ...orderRequestCategory, ...values };
              }
              return orderRequestCategory;
            })
          }
      );
    }
  });
};

export const useDeleteCompanyOrderRequestCategoryMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<undefined, ApiError, { companyId: string; orderRequestCategoryId: string }>(
    superadminCodebooksClient.deleteOrderRequestCategory,
    {
      onSuccess: (_, { companyId, orderRequestCategoryId }) => {
        queryClient.setQueryData<CompanyCodebooks | undefined>(
          getCompanyCodebooksQueryKeyBuilder(companyId),
          (oldData) =>
            oldData && {
              ...oldData,
              orderRequestCategories: oldData.orderRequestCategories.filter(
                (orderRequestCategory) => orderRequestCategory.id !== orderRequestCategoryId
              )
            }
        );
      },
      onError: (error) => handleApiErrorWithNotification(error)
    }
  );
};

// Custom Codebook

export const useAddCompanyCustomCodebookMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    CustomCodebook,
    ApiError,
    {
      companyId: string;
      values: CustomCodebookToAdd;
    }
  >(superadminCodebooksClient.addCustomCodebook, {
    onSuccess: (data, { companyId }) => {
      queryClient.setQueryData<CompanyCodebooks | undefined>(
        getCompanyCodebooksQueryKeyBuilder(companyId),
        (oldData) =>
          oldData && {
            ...oldData,
            customCodebooks: [...oldData.customCodebooks, data]
          }
      );
    }
  });
};

export const useEditCompanyCustomCodebookMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    undefined,
    ApiError,
    {
      companyId: string;
      customCodebookId: string;
      values: CustomCodebookToEdit;
    }
  >(superadminCodebooksClient.editCustomCodebook, {
    onSuccess: (data, { companyId, customCodebookId, values }) => {
      queryClient.setQueryData<CompanyCodebooks | undefined>(
        getCompanyCodebooksQueryKeyBuilder(companyId),
        (oldData) =>
          oldData && {
            ...oldData,
            customCodebooks: oldData.customCodebooks.map((x) => {
              if (x.id === customCodebookId) {
                return {
                  ...x,
                  ...values
                };
              }

              return x;
            })
          }
      );
    }
  });
};

export const useDeleteCompanyCustomCodebookMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<undefined, ApiError, { companyId: string; customCodebookId: string }>(
    superadminCodebooksClient.deleteCustomCodebook,
    {
      onSuccess: (data, { companyId, customCodebookId }) => {
        queryClient.setQueryData<CompanyCodebooks | undefined>(
          getCompanyCodebooksQueryKeyBuilder(companyId),
          (oldData) =>
            oldData && {
              ...oldData,
              customCodebooks: oldData.customCodebooks.filter((x) => x.id !== customCodebookId)
            }
        );
      },
      onError: (error) => handleApiErrorWithNotification(error)
    }
  );
};

export const useAddCompanyCustomCodebookItemMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    CustomCodebookItem,
    ApiError,
    {
      companyId: string;
      customCodebookId: string;
      values: CustomCodebookItemToAdd;
    }
  >(superadminCodebooksClient.addCustomCodebookItem, {
    onSuccess: (data, { companyId, customCodebookId }) => {
      queryClient.setQueryData<CompanyCodebooks | undefined>(
        getCompanyCodebooksQueryKeyBuilder(companyId),
        (oldData) =>
          oldData && {
            ...oldData,
            customCodebooks: oldData.customCodebooks.map((x) => {
              if (x.id === customCodebookId) {
                return {
                  ...x,
                  items: [...x.items, data]
                };
              }

              return x;
            })
          }
      );
    }
  });
};

export const useEditCompanyCustomCodebookItemMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    undefined,
    ApiError,
    {
      companyId: string;
      customCodebookId: string;
      customCodebookItemId: string;
      values: CustomCodebookItemToEdit;
    }
  >(superadminCodebooksClient.editCustomCodebookItem, {
    onSuccess: (data, { customCodebookId, companyId, customCodebookItemId, values }) => {
      queryClient.setQueryData<CompanyCodebooks | undefined>(
        getCompanyCodebooksQueryKeyBuilder(companyId),
        (oldData) =>
          oldData && {
            ...oldData,
            customCodebooks: oldData.customCodebooks.map((x) => {
              if (x.id === customCodebookId) {
                return {
                  ...x,
                  items: x.items.map((item) => {
                    if (item.id === customCodebookItemId) {
                      return {
                        ...item,
                        ...values
                      };
                    }

                    return item;
                  })
                };
              }

              return x;
            })
          }
      );
    }
  });
};

export const useDeleteCompanyCustomCodebookItemMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    undefined,
    ApiError,
    {
      companyId: string;
      customCodebookId: string;
      customCodebookItemId: string;
    }
  >(superadminCodebooksClient.deleteCustomCodebookItem, {
    onSuccess: (data, { customCodebookId, companyId, customCodebookItemId }) => {
      queryClient.setQueryData<CompanyCodebooks | undefined>(
        getCompanyCodebooksQueryKeyBuilder(companyId),
        (oldData) =>
          oldData && {
            ...oldData,
            customCodebooks: oldData.customCodebooks.map((x) => {
              if (x.id === customCodebookId) {
                return {
                  ...x,
                  items: x.items.filter((x) => x.id !== customCodebookItemId)
                };
              }

              return x;
            })
          }
      );
    },
    onError: (error) => handleApiErrorWithNotification(error)
  });
};

// Order category

export const useAddCompanyOrderCategoryMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<Codebook, ApiError, { companyId: string; values: CodebookToAdd }>(
    superadminCodebooksClient.addOrderCategory,
    {
      onSuccess: (data, { companyId }) => {
        queryClient.setQueryData<CompanyCodebooks | undefined>(
          getCompanyCodebooksQueryKeyBuilder(companyId),
          (oldData) =>
            oldData && {
              ...oldData,
              orderCategories: [...oldData.orderCategories, data]
            }
        );
      }
    }
  );
};

export const useEditCompanyOrderCategoryMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    undefined,
    ApiError,
    {
      companyId: string;
      categoryId: string;
      values: CodebookToEdit;
    }
  >(superadminCodebooksClient.editOrderCategory, {
    onSuccess: (data, { companyId, values, categoryId }) => {
      queryClient.setQueryData<CompanyCodebooks | undefined>(
        getCompanyCodebooksQueryKeyBuilder(companyId),
        (oldData) =>
          oldData && {
            ...oldData,
            orderCategories: oldData.orderCategories.map((orderCategory) => {
              if (orderCategory.id === categoryId) {
                return { ...orderCategory, ...values };
              }
              return orderCategory;
            })
          }
      );
    }
  });
};

export const useDeleteCompanyOrderCategoryMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<undefined, ApiError, { companyId: string; categoryId: string }>(
    superadminCodebooksClient.deleteOrderCategory,
    {
      onSuccess: (data, { companyId, categoryId }) => {
        queryClient.setQueryData<CompanyCodebooks | undefined>(
          getCompanyCodebooksQueryKeyBuilder(companyId),
          (oldData) =>
            oldData && {
              ...oldData,
              orderCategories: oldData.orderCategories.filter(
                (orderCategory) => orderCategory.id !== categoryId
              )
            }
        );
      },
      onError: (error) => handleApiErrorWithNotification(error)
    }
  );
};

// Service Object Custom State

export const useAddCompanyServiceObjectCustomStateMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<Codebook, ApiError, { companyId: string; values: CodebookToAdd }>({
    mutationFn: superadminCodebooksClient.addServiceObjectCustomState,
    onSuccess: (data, { companyId }) => {
      queryClient.setQueryData<CompanyCodebooks | undefined>(
        getCompanyCodebooksQueryKeyBuilder(companyId),
        (oldData) =>
          oldData && {
            ...oldData,
            serviceObjectCustomStates: [...oldData.serviceObjectCustomStates, data]
          }
      );
    }
  });
};

export const useEditCompanyServiceObjectCustomStateMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    undefined,
    ApiError,
    { companyId: string; serviceObjectCustomStateId: string; values: CodebookToEdit }
  >({
    mutationFn: superadminCodebooksClient.editServiceObjectCustomState,
    onSuccess: (data, { companyId, serviceObjectCustomStateId, values }) => {
      queryClient.setQueryData<CompanyCodebooks | undefined>(
        getCompanyCodebooksQueryKeyBuilder(companyId),
        (oldData) =>
          oldData && {
            ...oldData,
            serviceObjectCustomStates: oldData.serviceObjectCustomStates.map(
              (serviceObjectCustomState) =>
                serviceObjectCustomState.id === serviceObjectCustomStateId
                  ? { ...serviceObjectCustomState, ...values }
                  : serviceObjectCustomState
            )
          }
      );
    }
  });
};

export const useDeleteCompanyServiceObjectCustomStateMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    undefined,
    ApiError,
    { companyId: string; serviceObjectCustomStateId: string }
  >({
    mutationFn: superadminCodebooksClient.deleteServiceObjectCustomState,
    onSuccess: (data, { companyId, serviceObjectCustomStateId }) => {
      queryClient.setQueryData<CompanyCodebooks | undefined>(
        getCompanyCodebooksQueryKeyBuilder(companyId),
        (oldData) =>
          oldData && {
            ...oldData,
            serviceObjectCustomStates: oldData.serviceObjectCustomStates.filter(
              (serviceObjectCustomState) =>
                serviceObjectCustomState.id !== serviceObjectCustomStateId
            )
          }
      );
    },
    onError: (error) => handleApiErrorWithNotification(error)
  });
};

// Order Custom State

export const useAddCompanyOrderCustomStateMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<Codebook, ApiError, { companyId: string; values: CodebookToAdd }>(
    superadminCodebooksClient.addOrderCustomState,
    {
      onSuccess: (data, { companyId }) => {
        queryClient.setQueryData<CompanyCodebooks | undefined>(
          getCompanyCodebooksQueryKeyBuilder(companyId),
          (oldData) =>
            oldData && {
              ...oldData,
              orderCustomStates: [...oldData.orderCustomStates, data]
            }
        );
      }
    }
  );
};

export const useEditCompanyOrderCustomStateMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    undefined,
    ApiError,
    { companyId: string; orderCustomStateId: string; values: CodebookToEdit }
  >(superadminCodebooksClient.editOrderCustomState, {
    onSuccess: (data, { companyId, orderCustomStateId, values }) => {
      queryClient.setQueryData<CompanyCodebooks | undefined>(
        getCompanyCodebooksQueryKeyBuilder(companyId),
        (oldData) =>
          oldData && {
            ...oldData,
            orderCustomStates: oldData.orderCustomStates.map((orderCustomState) => {
              if (orderCustomState.id === orderCustomStateId) {
                return { ...orderCustomState, ...values };
              }
              return orderCustomState;
            })
          }
      );
    }
  });
};

export const useDeleteCompanyOrderCustomStateMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<undefined, ApiError, { companyId: string; orderCustomStateId: string }>(
    superadminCodebooksClient.deleteOrderCustomState,
    {
      onSuccess: (data, { orderCustomStateId, companyId }) => {
        queryClient.setQueryData<CompanyCodebooks | undefined>(
          getCompanyCodebooksQueryKeyBuilder(companyId),
          (oldData) =>
            oldData && {
              ...oldData,
              orderCustomStates: oldData.orderCustomStates.filter(
                (orderCustomState) => orderCustomState.id !== orderCustomStateId
              )
            }
        );
      },
      onError: (error) => handleApiErrorWithNotification(error)
    }
  );
};

// Order Request Custom State

export const useAddCompanyOrderRequestCustomStateMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<Codebook, ApiError, { companyId: string; values: CodebookToAdd }>(
    superadminCodebooksClient.addOrderRequestCustomState,
    {
      onSuccess: (data, { companyId }) => {
        queryClient.setQueryData<CompanyCodebooks | undefined>(
          getCompanyCodebooksQueryKeyBuilder(companyId),
          (oldData) =>
            oldData && {
              ...oldData,
              orderRequestCustomStates: [...oldData.orderRequestCustomStates, data]
            }
        );
      }
    }
  );
};

export const useEditCompanyOrderRequestCustomStateMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    undefined,
    ApiError,
    { companyId: string; orderRequestCustomStateId: string; values: CodebookToEdit }
  >(superadminCodebooksClient.editOrderRequestCustomState, {
    onSuccess: (data, { companyId, orderRequestCustomStateId, values }) => {
      queryClient.setQueryData<CompanyCodebooks | undefined>(
        getCompanyCodebooksQueryKeyBuilder(companyId),
        (oldData) =>
          oldData && {
            ...oldData,
            orderRequestCustomStates: oldData.orderRequestCustomStates.map(
              (orderRequestCustomState) => {
                if (orderRequestCustomState.id === orderRequestCustomStateId) {
                  return { ...orderRequestCustomState, ...values };
                }
                return orderRequestCustomState;
              }
            )
          }
      );
    }
  });
};

export const useDeleteCompanyOrderRequestCustomStateMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<undefined, ApiError, { companyId: string; orderRequestCustomStateId: string }>(
    superadminCodebooksClient.deleteOrderRequestCustomState,
    {
      onSuccess: (data, { orderRequestCustomStateId, companyId }) => {
        queryClient.setQueryData<CompanyCodebooks | undefined>(
          getCompanyCodebooksQueryKeyBuilder(companyId),
          (oldData) =>
            oldData && {
              ...oldData,
              orderRequestCustomStates: oldData.orderCustomStates.filter(
                (orderRequestCustomState) =>
                  orderRequestCustomState.id !== orderRequestCustomStateId
              )
            }
        );
      },
      onError: (error) => handleApiErrorWithNotification(error)
    }
  );
};

// Serviceobject Category

export const useAddCompanyServiceObjectCategoryMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    ServiceObjectCategoryWithStringTemplate,
    ApiError,
    {
      companyId: string;
      values: ServiceObjectCategoryWithStringTemplateToAdd;
    }
  >(superadminCodebooksClient.addServiceObjectCategory, {
    onSuccess: (data, { companyId }) => {
      queryClient.setQueryData<CompanyCodebooks | undefined>(
        getCompanyCodebooksQueryKeyBuilder(companyId),
        (oldData) =>
          oldData && {
            ...oldData,
            serviceObjectCategories: [...oldData.serviceObjectCategories, data]
          }
      );
    }
  });
};

export const useEditCompanyServiceObjectCategoryMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    undefined,
    ApiError,
    {
      companyId: string;
      categoryId: string;
      values: ServiceObjectCategoryWithStringTemplateToEdit;
    }
  >(superadminCodebooksClient.editServiceObjectCategory, {
    onSuccess: (data, { companyId, values, categoryId }) => {
      queryClient.setQueryData<CompanyCodebooks | undefined>(
        getCompanyCodebooksQueryKeyBuilder(companyId),
        (oldData) =>
          oldData && {
            ...oldData,
            serviceObjectCategories: oldData.serviceObjectCategories.map(
              (serviceObjectCategory) => {
                if (serviceObjectCategory.id === categoryId) {
                  return { ...serviceObjectCategory, ...values };
                }
                return serviceObjectCategory;
              }
            )
          }
      );
    }
  });
};

export const useDeleteCompanyServiceObjectCategoryMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<undefined, ApiError, { companyId: string; categoryId: string }>(
    superadminCodebooksClient.deleteServiceObjectCategory,
    {
      onSuccess: (data, { companyId, categoryId }) => {
        queryClient.setQueryData<CompanyCodebooks | undefined>(
          getCompanyCodebooksQueryKeyBuilder(companyId),
          (oldData) =>
            oldData && {
              ...oldData,
              serviceObjectCategories: oldData.serviceObjectCategories.filter(
                (serviceObjectCategory) => serviceObjectCategory.id !== categoryId
              )
            }
        );
      },
      onError: (error) => handleApiErrorWithNotification(error)
    }
  );
};

// Intervention category

export const useAddCompanyInterventionCategoryMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<Codebook, ApiError, { companyId: string; values: CodebookToAdd }>(
    superadminCodebooksClient.addInterventionCategory,
    {
      onSuccess: (data, { companyId }) => {
        queryClient.setQueryData<CompanyCodebooks | undefined>(
          getCompanyCodebooksQueryKeyBuilder(companyId),
          (oldData) =>
            oldData && {
              ...oldData,
              interventionCategories: [...oldData.interventionCategories, data]
            }
        );
      }
    }
  );
};

export const useEditCompanyInterventionCategoryMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    undefined,
    ApiError,
    {
      companyId: string;
      categoryId: string;
      values: CodebookToEdit;
    }
  >(superadminCodebooksClient.editInterventionCategory, {
    onSuccess: (data, { companyId, values, categoryId }) => {
      queryClient.setQueryData<CompanyCodebooks | undefined>(
        getCompanyCodebooksQueryKeyBuilder(companyId),
        (oldData) =>
          oldData && {
            ...oldData,
            interventionCategories: oldData.interventionCategories.map((interventionCategory) => {
              if (interventionCategory.id === categoryId) {
                return { ...interventionCategory, ...values };
              }
              return interventionCategory;
            })
          }
      );
    }
  });
};

export const useDeleteCompanyInterventionCategoryMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<undefined, ApiError, { companyId: string; categoryId: string }>(
    superadminCodebooksClient.deleteInterventionCategory,
    {
      onSuccess: (data, { companyId, categoryId }) => {
        queryClient.setQueryData<CompanyCodebooks | undefined>(
          getCompanyCodebooksQueryKeyBuilder(companyId),
          (oldData) =>
            oldData && {
              ...oldData,
              interventionCategories: oldData.interventionCategories.filter(
                (interventionCategory) => interventionCategory.id !== categoryId
              )
            }
        );
      },
      onError: (error) => handleApiErrorWithNotification(error)
    }
  );
};

// Intervention custom state

export const useAddCompanyInterventionCustomStateMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<Codebook, ApiError, { companyId: string; values: CodebookToAdd }>(
    superadminCodebooksClient.addInterventionCustomState,
    {
      onSuccess: (data, { companyId }) => {
        queryClient.setQueryData<CompanyCodebooks | undefined>(
          getCompanyCodebooksQueryKeyBuilder(companyId),
          (oldData) =>
            oldData && {
              ...oldData,
              interventionCustomStates: [...oldData.interventionCustomStates, data]
            }
        );
      }
    }
  );
};

export const useEditCompanyInterventionCustomStateMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    undefined,
    ApiError,
    { companyId: string; interventionCustomStateId: string; values: CodebookToEdit }
  >(superadminCodebooksClient.editInterventionCustomState, {
    onSuccess: (data, { companyId, interventionCustomStateId, values }) => {
      queryClient.setQueryData<CompanyCodebooks | undefined>(
        getCompanyCodebooksQueryKeyBuilder(companyId),
        (oldData) =>
          oldData && {
            ...oldData,
            interventionCustomStates: oldData.interventionCustomStates.map(
              (interventionCustomState) => {
                if (interventionCustomState.id === interventionCustomStateId) {
                  return { ...interventionCustomState, ...values };
                }
                return interventionCustomState;
              }
            )
          }
      );
    }
  });
};

export const useDeleteCompanyInterventionCustomStateMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<undefined, ApiError, { companyId: string; interventionCustomStateId: string }>(
    superadminCodebooksClient.deleteInterventionCustomState,
    {
      onSuccess: (data, { interventionCustomStateId, companyId }) => {
        queryClient.setQueryData<CompanyCodebooks | undefined>(
          getCompanyCodebooksQueryKeyBuilder(companyId),
          (oldData) =>
            oldData && {
              ...oldData,
              interventionCustomStates: oldData.interventionCustomStates.filter(
                (interventionCustomState) =>
                  interventionCustomState.id !== interventionCustomStateId
              )
            }
        );
      },
      onError: (error) => handleApiErrorWithNotification(error)
    }
  );
};

// Group

export const useAddCompanyGroupMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<Group, ApiError, { companyId: string; values: GroupToAdd }>(
    superadminCodebooksClient.addGroup,
    {
      onSuccess: (data, { companyId }) => {
        queryClient.setQueryData<CompanyCodebooks | undefined>(
          getCompanyCodebooksQueryKeyBuilder(companyId),
          (oldData) =>
            oldData && {
              ...oldData,
              groups: [...oldData.groups, data]
            }
        );
      }
    }
  );
};

export const useEditCompanyGroupMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    undefined,
    ApiError,
    {
      companyId: string;
      groupId: string;
      values: GroupToEdit;
    }
  >(superadminCodebooksClient.editGroup, {
    onSuccess: (data, { companyId, values, groupId }) => {
      queryClient.setQueryData<CompanyCodebooks | undefined>(
        getCompanyCodebooksQueryKeyBuilder(companyId),
        (oldData) =>
          oldData && {
            ...oldData,
            groups: oldData.groups.map((group) => {
              if (group.id === groupId) {
                return { ...group, ...values };
              }
              return group;
            })
          }
      );
    }
  });
};

export const useDeleteCompanyGroupMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<undefined, ApiError, { companyId: string; groupId: string }>(
    superadminCodebooksClient.deleteGroup,
    {
      onSuccess: (data, { companyId, groupId }) => {
        queryClient.setQueryData<CompanyCodebooks | undefined>(
          getCompanyCodebooksQueryKeyBuilder(companyId),
          (oldData) =>
            oldData && {
              ...oldData,
              groups: oldData.groups.filter((group) => group.id !== groupId)
            }
        );
      },
      onError: (error) => handleApiErrorWithNotification(error)
    }
  );
};

export const useEditCompanyGroupMembersMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    undefined,
    ApiError,
    {
      companyId: string;
      groupId: string;
      values: GroupUsersToManage;
    }
  >(superadminCodebooksClient.manageGroupUsers, {
    onSuccess: (data, { companyId, values, groupId }) => {
      queryClient.setQueryData<CompanyCodebooks | undefined>(
        getCompanyCodebooksQueryKeyBuilder(companyId),
        (oldData) =>
          oldData && {
            ...oldData,
            groups: oldData.groups.map((group) => {
              if (group.id === groupId) {
                return { ...group, users: values.users };
              }
              return group;
            })
          }
      );
    }
  });
};

// Skill

export const useAddCompanySkillMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<Skill, ApiError, { companyId: string; values: SkillToAdd }>(
    superadminCodebooksClient.addSkill,
    {
      onSuccess: (data, { companyId }) => {
        queryClient.setQueryData<CompanyCodebooks | undefined>(
          getCompanyCodebooksQueryKeyBuilder(companyId),
          (oldData) =>
            oldData && {
              ...oldData,
              skills: [...oldData.skills, data]
            }
        );
      }
    }
  );
};

export const useEditCompanySkillMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    undefined,
    ApiError,
    {
      companyId: string;
      skillId: string;
      values: CodebookToEdit;
    }
  >(superadminCodebooksClient.editSkill, {
    onSuccess: (data, { companyId, values, skillId }) => {
      queryClient.setQueryData<CompanyCodebooks | undefined>(
        getCompanyCodebooksQueryKeyBuilder(companyId),
        (oldData) =>
          oldData && {
            ...oldData,
            skills: oldData.skills.map((skill) => {
              if (skill.id === skillId) {
                return { ...skill, ...values };
              }
              return skill;
            })
          }
      );
    }
  });
};

export const useDeleteCompanySkillMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<undefined, ApiError, { companyId: string; skillId: string }>(
    superadminCodebooksClient.deleteSkill,
    {
      onSuccess: (data, { companyId, skillId }) => {
        queryClient.setQueryData<CompanyCodebooks | undefined>(
          getCompanyCodebooksQueryKeyBuilder(companyId),
          (oldData) =>
            oldData && {
              ...oldData,
              skills: oldData.skills.filter((skill) => skill.id !== skillId)
            }
        );
      },
      onError: (error) => handleApiErrorWithNotification(error)
    }
  );
};

export const useEditCompanySkillMembersMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    undefined,
    ApiError,
    {
      companyId: string;
      skillId: string;
      values: SkillUsersToManage;
    }
  >(superadminCodebooksClient.manageSkillUsers, {
    onSuccess: (data, { companyId, values, skillId }) => {
      queryClient.setQueryData<CompanyCodebooks | undefined>(
        getCompanyCodebooksQueryKeyBuilder(companyId),
        (oldData) =>
          oldData && {
            ...oldData,
            skills: oldData.skills.map((skill) => {
              if (skill.id === skillId) {
                return { ...skill, users: values.users };
              }
              return skill;
            })
          }
      );
    }
  });
};

// Codebooks batch add

export const useAddCompanyCodebooksBatchMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    undefined,
    ApiError,
    { companyId: string; values: SuperadminCompanyCodebooksToSet }
  >(superadminCodebooksClient.addCodebooksBatch, {
    onSuccess: (data, { companyId }) =>
      queryClient.invalidateQueries(getCompanyCodebooksQueryKeyBuilder(companyId))
  });
};

// Companies codebook

export const useGetCompaniesCodebookQuery = () => {
  return useQuery<SuperadminCompanyCodebookItem[], ApiError>(
    getCompaniesCodebookQueryKeyBuilder(),
    () => superadminCompaniesClient.getCompaniesCodebook()
  );
};

export const useGetCompanyDownloadUriTemplates = (companyId: string) => {
  return useQuery<StorageDownloadTemplates, ApiError>(
    getSuperadminDownloadUriTemplatesKeyBuilder(companyId),
    () => superadminCompaniesClient.getCompanyDownloadUriTemplates(companyId),
    {
      staleTime: GLOBAL_QUERY_STALE_TIME
    }
  );
};

export const useGetCompanyBackgroundTasksQuery = (
  id: string,
  params: SuperadminBackgroundTaskInfoQuery
) => {
  return useQuery<TableResult<SuperadminBackgroundTaskInfo>>(
    getTableQueryKey(backgroundTasksKey, params, id),
    () => backgroundTasksClient.getBackgroundTasks({ ...params, companyId: id })
  );
};

export const useGetCompanyBackgroundTaskDetailQuery = (id: string) => {
  return useQuery<SuperadminBackgroundTaskInfoDetail>(
    getSuperadminBackgroundTaskDetailKeyBuilder(id),
    () => backgroundTasksClient.getBackgroundTaskDetail(id)
  );
};

const isBackgroundTaskTerminal = (state?: BackgroundTaskState) =>
  state === 'Success' || state === 'Failed' || state === 'Aborted';

export const getSuperadminImportTaskStateKeyBuilder = (importTaskId: string) => [
  'superadminImportTaskState',
  importTaskId
];

// Polls a single import task's state from the superadmin endpoint (used by the import-request
// card in the superadmin ticket thread, which can't read the company-scoped import endpoint).
export const useGetSuperadminImportTaskStateQuery = (importTaskId?: string) => {
  return useQuery<SuperadminBackgroundTaskInfoDetail>(
    getSuperadminImportTaskStateKeyBuilder(importTaskId ?? ''),
    () => backgroundTasksClient.getBackgroundTaskDetail(importTaskId!),
    {
      enabled: !!importTaskId,
      refetchInterval: (data) => (isBackgroundTaskTerminal(data?.state) ? false : 3000)
    }
  );
};

// Permissions

export const useGetCompanyUserPermissionsQuery = (id: string) => {
  return useQuery<SuperadminUserSuperadminPermissionResult>(
    getCompanyUsersPermissionsKeyBuilder(id),
    () => superadminPermissionsClient.getUsersPermissions(id)
  );
};

export const useSetCompanyUserPermissionsMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    undefined,
    ApiError,
    { companyId: string; values: SuperadminUserSuperadminPermissionToEdit[] }
  >(superadminPermissionsClient.setUsersPermissions, {
    onSuccess: (data, { companyId, values }) =>
      queryClient.setQueryData<SuperadminUserSuperadminPermissionResult>(
        getCompanyUsersPermissionsKeyBuilder(companyId),
        {
          userSuperadminPermissions: values
        }
      )
  });
};

// Company statistics

export const useGetCompanyStatisticsQuery = (companyId: string) => {
  return useQuery<SuperadminCompanyStats>(getCompanyStatisticsKeyBuilder(companyId), () =>
    superadminCompaniesClient.getCompanyStatistics(companyId)
  );
};
